import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

type FilterValue = string | string[] | undefined;

type FilterValues = Record<string, FilterValue>;

interface FilterDefinition {
  id: string;
  label: string;
}

interface FiltersContextValue {
  filters: FilterDefinition[];
  values: FilterValues;
  visibleFilters: string[];
  hasActiveFilters: boolean;
  searchRef: RefObject<HTMLInputElement | null>;
  getValue: (id: string) => FilterValue;
  setValue: (id: string, value: FilterValue) => void;
  setFilterVisible: (id: string, visible: boolean) => void;
  clearAll: () => void;
}

interface FiltersProviderProps {
  filters: FilterDefinition[];
  values: FilterValues;
  onValuesChange: (values: FilterValues) => void;
  visibleFilters: string[];
  onVisibleFiltersChange: (visibleFilters: string[]) => void;
  children: ReactNode;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

function isValueSet(value: FilterValue) {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

function FiltersProvider({
  filters,
  values,
  onValuesChange,
  visibleFilters,
  onVisibleFiltersChange,
  children,
}: FiltersProviderProps) {
  const searchRef = useRef<HTMLInputElement>(null);

  const getValue = useCallback((id: string) => values[id], [values]);

  const setValue = useCallback(
    (id: string, value: FilterValue) => {
      onValuesChange({ ...values, [id]: value });
    },
    [onValuesChange, values]
  );

  const setFilterVisible = useCallback(
    (id: string, visible: boolean) => {
      onVisibleFiltersChange(
        visible
          ? [...visibleFilters, id]
          : visibleFilters.filter((item) => item !== id)
      );

      if (!visible && isValueSet(values[id])) {
        onValuesChange({ ...values, [id]: undefined });
      }
    },
    [onValuesChange, onVisibleFiltersChange, values, visibleFilters]
  );

  const clearAll = useCallback(() => {
    onValuesChange(
      Object.fromEntries(Object.keys(values).map((id) => [id, undefined]))
    );
    searchRef.current?.focus();
  }, [onValuesChange, values]);

  const hasActiveFilters = Object.values(values).some(isValueSet);

  const contextValue = useMemo(
    () => ({
      filters,
      values,
      visibleFilters,
      hasActiveFilters,
      searchRef,
      getValue,
      setValue,
      setFilterVisible,
      clearAll,
    }),
    [
      clearAll,
      filters,
      getValue,
      hasActiveFilters,
      setFilterVisible,
      setValue,
      values,
      visibleFilters,
    ]
  );

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
}

function useFilters() {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error("useFilters must be used within FiltersProvider");
  }

  return context;
}

export { FiltersProvider, useFilters };
export type {
  FilterDefinition,
  FiltersProviderProps,
  FilterValue,
  FilterValues,
};
